// The state-changing command list + `isStateChangingEngineCommand` (the #236
// remote-lock scope) live once, in the bt-common contracts file.

/**
 * #236 (3) — client-side dedup of repeat MQTT deliveries of the same
 * `_engineCmdId`. The same command is delivered ~3× per engine (shared user-level
 * topic + per-client topic / backend republish); with this cache the 2nd/3rd
 * deliveries are dropped locally — no re-execution, no network. Unlike the remote
 * lock's `deduplicated:true` response, a dropped duplicate here publishes NOTHING,
 * so the first delivery's real result is what the caller receives (safe for reads).
 */
export class EngineCmdDedupCache {
  private readonly seen = new Map<string, number>(); // engineCmdId -> firstSeenAt (ms)

  constructor(
    private readonly ttlMs = 60_000,
    private readonly maxEntries = 2_000,
    private readonly nowFn: () => number = () => Date.now(),
  ) {}

  /**
   * Record `engineCmdId`. Returns true when it was ALREADY seen within the TTL
   * (i.e. this is a duplicate delivery to drop); false when it is new (and now
   * recorded). Empty/absent ids are never treated as duplicates.
   */
  markSeen(engineCmdId: string | null | undefined): boolean {
    if (!engineCmdId) return false;
    const now = this.nowFn();
    const prev = this.seen.get(engineCmdId);
    if (prev !== undefined && now - prev < this.ttlMs) {
      return true; // duplicate within window
    }
    this.pruneExpired(now);
    this.seen.set(engineCmdId, now);
    if (this.seen.size > this.maxEntries) {
      // evict oldest insertion (Map preserves insertion order)
      const oldest = this.seen.keys().next().value as string | undefined;
      if (oldest !== undefined) this.seen.delete(oldest);
    }
    return false;
  }

  private pruneExpired(now: number): void {
    for (const [id, ts] of this.seen) {
      if (now - ts >= this.ttlMs) this.seen.delete(id);
      else break; // insertion-ordered → the rest are newer
    }
  }
}
